import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button } from '@mui/material';
import {
  Hub as HubIcon, EditRounded, CheckCircleRounded, CloudDoneRounded, SaveRounded,
} from '@mui/icons-material';
import whatsappService from '../services/whatsappService';
import useSnackbar from '../hooks/useSnackbar';
import PageLayout from '../components/ui/PageLayout';
import LoadingState from '../components/ui/LoadingState';
import AppSnackbar from '../components/ui/AppSnackbar';
import ProductCatalog from '../components/billing/ProductCatalog';
import { useMeter } from '../hooks/useEntitlements';
import { pageContainer } from '../styles/pageStyles';
import { slate } from '../constants/uiTokens';

import WhatsAppHero from './integrations/WhatsAppHero';
import WhatsAppConfig from './integrations/WhatsAppConfig';
import MessageHistoryCard from './integrations/MessageHistoryCard';
import CreditChip from './integrations/CreditChip';
import BalanceBanner from './integrations/BalanceBanner';
import Txt from './integrations/Txt';

const DEFAULT_SETTINGS = {
  enabled: false,
  templateStyle: 2,
  messageNotes: '',
  notifyOnPublicCreate: true,
  notifyOnPrivateCreate: true,
  reminderEnabled: false,
  reminderHoursBefore: 24,
  reminderRespectBusinessHours: true,
  reminderBusinessStart: 8,
  reminderBusinessEnd: 18,
  ownerNotifyEnabled: false,
  ownerPhone: '',
  ownerNotifyOnCreate: true,
  ownerNotifyOnConfirm: true,
  ownerNotifyOnCancel: true,
};

// Exemplo mostrado no preview quando o catálogo de modelos ainda não carregou.
const FALLBACK_SAMPLE = `Oi, Maria! 😊 Deu tudo certo, o seu horário está confirmado!

✨ Serviço: Corte de cabelo
💈 Profissional: Ana
📍 Local: Salão X — Rua Y, 123
🗓️ Quando: 25/06/2026 às 14:30

Precisa mudar alguma coisa? É só acessar app.agendario.com.br/a/123.

Até logo! 💚`;

function clampHour(v, fallback) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(24, Math.max(0, n));
}

// Monta o payload normalizado a partir de um snapshot do form.
function buildPayload(src) {
  return {
    ...src,
    messageNotes: src.messageNotes?.trim() ? src.messageNotes.trim() : null,
    reminderHoursBefore: Math.min(168, Math.max(1, parseInt(src.reminderHoursBefore, 10) || 24)),
    reminderBusinessStart: clampHour(src.reminderBusinessStart, 8),
    reminderBusinessEnd: clampHour(src.reminderBusinessEnd, 18),
  };
}

// Texto da pré-visualização a partir do modelo selecionado + orientações.
function buildPreview(model, notes) {
  const trimmed = notes?.trim();
  if (!model) return FALLBACK_SAMPLE;
  return trimmed
    ? (model.previewCreatedWithNotes || '').replace('{{ORIENTACOES}}', trimmed)
    : (model.previewCreated || '');
}

export default function IntegrationsPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const { balance: waBalance, low: waLow } = useMeter('whatsapp_messages');
  const catalogRef = useRef(null);

  // ── Settings state ──────────────────────────────────────────────────────────
  const [form, setForm]                       = useState(DEFAULT_SETTINGS);
  const [savedForm, setSavedForm]             = useState(DEFAULT_SETTINGS); // baseline persistido
  const [dirty, setDirty]                     = useState(false);
  const [justSaved, setJustSaved]             = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings]   = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [models, setModels]                   = useState([]);

  // ── Messages state ──────────────────────────────────────────────────────────
  const [messages, setMessages]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [summary, setSummary]     = useState(null);

  // ── Load settings on mount ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await whatsappService.getSettings();
        const merged = { ...DEFAULT_SETTINGS, ...data, messageNotes: data.messageNotes ?? '', ownerPhone: data.ownerPhone ?? '' };
        setForm(merged);
        setSavedForm(merged);
        try { setModels(await whatsappService.getModels()); } catch { /* nomes/preview caem no fallback */ }
      } catch {
        // Silencioso — mantém defaults
      } finally {
        setLoadingSettings(false);
      }
    })();
  }, []);

  // ── Load messages when page/rowsPerPage change ──────────────────────────────
  const loadMessages = useCallback(async (p, rpp) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const data = await whatsappService.listMessages(p + 1, rpp); // API é 1-indexed
      setMessages(data.items ?? []);
      setTotal(data.total ?? 0);
      setSummary(data.summary ?? null);
    } catch {
      setMessagesError('Não foi possível carregar o histórico. Tente novamente.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadMessages(page, rowsPerPage);
  }, [page, rowsPerPage, loadMessages]);

  // ── Form helpers (auto-save híbrido) ─────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setJustSaved(false);
  }, []);

  // Salva os campos de config (explícito). O liga/desliga é auto-save à parte.
  const handleSave = useCallback(async () => {
    setSavingSettings(true);
    try {
      await whatsappService.updateSettings(buildPayload(form));
      setSavedForm(form);
      setDirty(false);
      setJustSaved(true);
      showSuccess('Configurações de WhatsApp salvas.');
    } catch {
      showError('Não foi possível salvar as configurações. Tente novamente.');
    } finally {
      setSavingSettings(false);
    }
  }, [form, showSuccess, showError]);

  const handleDiscard = useCallback(() => {
    setForm(savedForm);
    setDirty(false);
    setJustSaved(false);
  }, [savedForm]);

  // Liga/desliga persistindo na hora a partir do baseline (não arrasta edições não salvas).
  const handleToggleEnabled = useCallback(async (next) => {
    setForm((f) => ({ ...f, enabled: next }));
    setTogglingEnabled(true);
    try {
      await whatsappService.updateSettings(buildPayload({ ...savedForm, enabled: next }));
      setSavedForm((s) => ({ ...s, enabled: next }));
      showSuccess(next ? 'WhatsApp ativado.' : 'WhatsApp desativado.');
    } catch {
      setForm((f) => ({ ...f, enabled: savedForm.enabled })); // reverte
      showError('Não foi possível atualizar. Tente novamente.');
    } finally {
      setTogglingEnabled(false);
    }
  }, [savedForm, showSuccess, showError]);

  // ── Pagination handlers ─────────────────────────────────────────────────────
  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const scrollToCatalog = () => catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const selectedModel = models.find((m) => m.id === form.templateStyle);
  const previewText = buildPreview(selectedModel, form.messageNotes) || FALLBACK_SAMPLE;
  const balanceState = (waBalance ?? 0) === 0 ? 'zero' : (waLow ? 'low' : 'ok');

  // Estado da save bar
  const saveState = dirty ? 'dirty' : (justSaved ? 'saved' : 'clean');
  const SAVE_UI = {
    dirty: { icon: <EditRounded sx={{ fontSize: 20, color: 'warning.main' }} />, text: 'Você tem alterações não salvas' },
    saved: { icon: <CheckCircleRounded sx={{ fontSize: 20, color: 'success.main' }} />, text: 'Configurações salvas' },
    clean: { icon: <CloudDoneRounded sx={{ fontSize: 20, color: 'success.main' }} />, text: 'Tudo salvo' },
  }[saveState];

  return (
    <PageLayout
      title="Integrações"
      subtitle="Conecte canais de comunicação para automatizar o contato com seus clientes."
      icon={<HubIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32, color: 'primary.dark' }} />}
      actionButton={
        !loadingSettings && form.enabled
          ? <CreditChip balance={waBalance ?? 0} state={balanceState} onBuy={scrollToCatalog} />
          : null
      }
    >
      <Box sx={pageContainer}>
        {loadingSettings ? (
          <LoadingState message="Carregando integrações..." />
        ) : !form.enabled ? (
          /* ── Estado DESATIVADO: só o hero. Pacotes NÃO são vendidos antes de ativar (fix 5). ── */
          <WhatsAppHero
            previewText={previewText}
            previewShowButtons={false}
            onActivate={() => handleToggleEnabled(true)}
            activating={togglingEnabled}
          />
        ) : (
          /* ── Estado ATIVADO: config + preview + pacotes + histórico + save bar ── */
          <>
            {(balanceState === 'zero' || balanceState === 'low') && (
              <BalanceBanner state={balanceState} balance={waBalance ?? 0} onBuy={scrollToCatalog} sx={{ mb: 2 }} />
            )}

            <WhatsAppConfig
              form={form}
              updateField={updateField}
              models={models}
              onDisable={() => handleToggleEnabled(false)}
              togglingEnabled={togglingEnabled}
              previewText={previewText}
              previewShowButtons={form.reminderEnabled}
              previewEmpty={!previewText?.trim()}
            />

            <Box ref={catalogRef} sx={{ mt: 2 }}>
              <ProductCatalog meter="whatsapp_messages" balance={waBalance ?? 0} balanceState={balanceState} />
            </Box>

            <Box sx={{ mt: 2 }}>
              <MessageHistoryCard
                messages={messages}
                total={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                loading={loadingMessages}
                error={messagesError}
                setError={setMessagesError}
                summary={summary}
              />
            </Box>

            {/* Save bar sticky (auto-save híbrido: só os campos de config) */}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 30,
                mt: 2,
                mx: { xs: -2, md: 0 },
                px: { xs: 2, md: 3 },
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                flexWrap: 'wrap',
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: dirty ? '#cdd9e6' : 'divider',
                boxShadow: '0 -2px 12px rgba(16,24,40,.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                {SAVE_UI.icon}
                <Txt sx={{ fontSize: 13, color: slate[600] }}>{SAVE_UI.text}</Txt>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25 }}>
                <Button onClick={handleDiscard} disabled={!dirty || savingSettings} sx={{ color: slate[600] }}>
                  Descartar
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleSave}
                  disabled={!dirty || savingSettings}
                  startIcon={<SaveRounded sx={{ fontSize: 19 }} />}
                  sx={{ borderRadius: '8px', fontWeight: 600 }}
                >
                  Salvar configurações
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
